<script setup lang="ts">
import type {
  BreadcrumbCrumbsItem,
  BreadcrumbItemProps,
} from '/#/components/base/breadcrumb/breadcrumb'
import type { ComputedRef } from 'vue'

import flatMap from 'lodash-es/flatMap'
import isArray from 'lodash-es/isArray'
import isString from 'lodash-es/isString'
import last from 'lodash-es/last'
import slice from 'lodash-es/slice'
import some from 'lodash-es/some'
import take from 'lodash-es/take'

const props = defineProps<BreadcrumbItemProps>()

const router = useRouter()

const { smallerThan } = useScreenSize()

/**
 * @var allItems {ComputedRef<allItems>}
 * @description Holds all items as flattened.
 */
const allItems: ComputedRef<BreadcrumbCrumbsItem[]> = computed(() =>
  flatMap(props.crumbs),
)

/**
 * @method breadCrumbItemsWithEllipsis
 * @description Show dropdown ellipsis for more than 3 breadcrumb.
 */
const breadCrumbItemsWithEllipsis: ComputedRef<BreadcrumbCrumbsItem[]> = computed(() => {
  const isAnyArrayBreadcrumbItemsExist = some(props.crumbs, isArray)
  if (isAnyArrayBreadcrumbItemsExist) return props.crumbs

  let items: BreadcrumbCrumbsItem[] = take(props.crumbs, 2)
  const dropdownItems = slice(props.crumbs, items.length, props.crumbs.length - 1)
  items.push(dropdownItems as BreadcrumbCrumbsItem)
  items.push(last(props.crumbs) as BreadcrumbCrumbsItem)
  return items
})

/**
 * @name breadCrumbItemsInSmallerScreen
 * @description Generates breadcrumb items for smaller screens.
 */
const breadCrumbItemsInSmallerScreen: ComputedRef<Recordable[]> = computed(() => {
  if (!props.crumbs) return []
  const items = []

  // Adds first item
  items.push(props.crumbs[0])

  const otherItems = props.crumbs.slice(1, props.crumbs.length)
  if (otherItems.length > 0) items.push(otherItems)

  return items
})

/**
 * @method breadCrumbItems
 * @description Multiple breadcrumb items consideration
 */
const breadCrumbItems: ComputedRef<BreadcrumbCrumbsItem[]> = computed(() => {
  if (smallerThan('md') && props.responsive) return breadCrumbItemsInSmallerScreen.value
  if (allItems.value.length <= 3) return props.crumbs
  return breadCrumbItemsWithEllipsis.value
})

/**
 * @name getItemClasses
 * @description Computes dynamic CSS classes for breadcrumb items
 */
const appearance: ComputedRef<
  (item: BreadcrumbCrumbsItem, key: number) => Record<string, boolean>
> = computed(() => {
  return (item: BreadcrumbCrumbsItem, key: number): Record<string, boolean> => ({
    'has-dropdown': Boolean(isArray(item) && item.length > 0),
    'is-last': Boolean(key === breadCrumbItems.value.length - 1),
  })
})

/**
 * @method redirectToPage
 * @param item {BreadcrumbItem} Holds breadcrumb item.
 * @description Handles redirection operations.
 */
async function redirectToPage(item: BreadcrumbCrumbsItem) {
  if (item.to) {
    const to = isString(item.to) ? { name: item.to } : item.to
    await router.push(to)
    return
  }

  await router.push({ path: item.path as string })
}
</script>

<template>
  <li
    v-for="(item, key) in breadCrumbItems"
    :key="key"
    :aria-current="key === breadCrumbItems.length - 1 ? 'page' : undefined"
    itemprop="itemListElement"
    itemscope
    itemtype="https://schema.org/ListItem"
    :class="appearance(item, key)"
    class="breadcrumb-item"
  >
    <slot name="breadcrumb-item" :item="item" :index="key">
      <Placeload v-if="isLoading" type="line" />
      <Dropdown
        v-else-if="isArray(item) && item.length > 0"
       
        position="bottom"
        after-close
        title="..."
        variant="link"
        color="slate"
        class="breadcrumb-dropdown"
      >
        <template #content>
          <ul class="breadcrumb-path-list">
            <li
              v-for="menuItem in item"
              :key="menuItem.label"
             
              @click="redirectToPage(menuItem)"
              @keydown.enter="redirectToPage(menuItem)"
            >
              <TruncatedText
                v-if="item.truncateLength"
                :key="menuItem.label"
                :value="menuItem.label as string"
                :text-length="item.truncateLength"
              />
              <span v-else>
                {{ menuItem.label }}
              </span>
            </li>
          </ul>
        </template>
      </Dropdown>

      <Link
        v-else-if="item.to"
        class="breadcrumb-item is-link"
        :class="{ 'is-last': key === breadCrumbItems.length - 1 }"
       
        itemprop="item"
        :to="item.to"
      >
        <span
          v-if="!!item.icon"
          :class="[item.hideLabel && !!item.icon && 'is-solo']"
         
        >
          <Placeload v-if="isLoading" type="icon" />
          <Icon v-else :name="item.icon" size="md" />
        </span>
        <meta
          v-if="item.hideLabel && !!item.icon"
          itemprop="name"
          :content="item.label"
        />
        <span v-else itemprop="name">
          <Placeload v-if="isLoading" type="line" />
          <slot v-else name="breadcrumb-item-label" :item="item" :index="key">
            <TruncatedText
              v-if="item.truncateLength"
              :key="item.label"
              :value="item.label as string"
              :text-length="item.truncateLength"
            />

            <span v-else>
              {{ item.label }}
            </span>
          </slot>
        </span>

        <meta itemprop="position" :content="`${key + 1}`" />
      </Link>

      <span
        v-else
        class="breadcrumb-item"
        :class="{ 'is-last': key === breadCrumbItems.length - 1 }"
       
      >
        <span
          v-if="!!item.icon"
          :class="[item.hideLabel && !!item.icon && 'is-solo']"
         
        >
          <Placeload v-if="isLoading" type="icon" />
          <Icon v-else :name="item.icon" size="md" />
        </span>
        <meta v-if="item.hideLabel && item.icon" itemprop="name" :content="item.label" />
        <span v-else itemprop="name">
          <Placeload v-if="isLoading" type="line" />

          <slot v-else name="breadcrumb-item-label" :item="item" :index="key">
            <TruncatedText
              v-if="item.truncateLength"
              :key="item.label"
              :value="item.label as string"
              :text-length="item.truncateLength"
            />
            <span v-else>
              {{ item.label }}
            </span>
          </slot>
        </span>

        <meta itemprop="position" :content="`${key + 1}`" />
      </span>
    </slot>
  </li>
</template>
